import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { supabase, setIntentionalSignOut } from "@/lib/supabase";
import {
	OrganizationEntityType,
	ProfileEntityType,
	SupabaseReadTableEnum,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";

export type Lab = {
	name: string;
	labOrganizationId: string;
};

// Import form types from dentlab
import { ExtendedFormTemplate } from "@/dentlab/src/pages/form-builder/types/formTypes";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useConnectStore } from "@/dentlab/src/store/Connect";

export interface CentralSlice {
	labsLookup: Record<string, Lab>;
	userId: string | null;
	userEmail: string | null;
	clientId: string | null;
	profile: ProfileEntityType | null;
	organization: OrganizationEntityType | null;
	initialized: boolean;
	appInitializedAt: string | null;
	labForms: Record<string, ExtendedFormTemplate[]>; // Forms organized by organizationId
	// Bumped each time the user navigates to a different vendor (Labor) page.
	// Vendor-scoped fetches capture this at start and discard their results on mismatch.
	vendorFetchEpoch: number;
	setClientId: (clientId: string) => void;
	initialize: () => Promise<void>;
	getSession: () => Promise<string | null>;
	getProfile: () => Promise<void>;
	getOrganization: (organizationId: string) => Promise<void>;
	handleSignOut: () => Promise<void>;
	getLabs: () => Promise<void>;
	getLabForms: (labId: string) => Promise<void>;
}

export const createCentralStore: StateCreator<CentralSlice> = (set, get) => ({
	userId: null,
	userEmail: null,
	profile: null,
	organization: null,
	initialized: false,
	appInitializedAt: null,
	clientId: null,
	labsLookup: {},
	labForms: {},
	vendorFetchEpoch: 0,
	setClientId: (clientId: string) =>
		set({
			clientId,
		}),
	initialize: async () => {
		const {
			getSession,
			getProfile,
			getOrganization,
			getLabs,
		} = get();

		if (get().initialized) {
			Logger.warn("Central store already initialized");
			return;
		}

		set({
			appInitializedAt: new Date().toISOString(),
		});

		const organizationId = await getSession();
		if (!organizationId) return;

		await getOrganization(organizationId);
		await getLabs();
		await getProfile();

		// Initialize message store with auth data
		const { userId, organization } = get();
		if (userId && organization?.id) {
			const { initializeAuth, setGetNotificationNavigationPath } = useRealtimeStore.getState();
			initializeAuth(userId, organization, 0);

			// The realtime channel is shared between dentlab and dentconnect
			// So we want to make it aware of how it should navigate from notifications for our app context
			setGetNotificationNavigationPath((channel) => {
				if (channel.channel_type === 'job' && channel.job_id) {
					return `/vendor/${channel.vendor_org_id}/job/${channel.job_id}`;
				}
				if (channel.channel_type === 'job_request' || channel.channel_type === 'job_request_denied') {
					return `/vendor/${channel.vendor_org_id}/job/request/${channel.id}`;
				}
				// Only other type is "general_chat"
				return `/vendor/${channel.vendor_org_id}/chat`;
			});
		}

		await useRealtimeStore.getState().getProfilesForChat();

		set({
			initialized: true,
		});

		useRealtimeStore.getState().fetchLatestChannels();

		if (organization?.id) {
			useConnectStore.getState().fetchConnectedVendors(organization.id);
			useConnectStore.getState().fetchBrands(organization.id);
		}
	},
	getSession: async () => {
		const { data } = await supabase.auth.getSession();

		set({
			userId: data.session?.user.id,
			userEmail: data.session?.user.email ?? "",
		});

		return data.session?.user.app_metadata.organization_id;
	},
	getProfile: async () => {
		const { userId } = get();
		if (!userId) {
			Logger.error("User ID is not set");
			return;
		}
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.select('*')
			.eq("id", userId)
			.single();

		if (error) {
			Logger.error(error);
			return;
		}

		const profile = data as ProfileEntityType;
		set({ profile });
	},
	getOrganization: async (organizationId: string) => {
		const { data, error } = await supabase
			.from(SupabaseReadTableEnum.ORGANIZATIONS_VIEW)
			.select()
			.eq("id", organizationId)
			.single();

		if (error) {
			Logger.error(error);
			return;
		}
		if (data) {
			set({
				organization: data,
			});
		}
	},
	handleSignOut: async () => {
		setIntentionalSignOut(true);
		try {
			const { error } = await supabase.auth.signOut();
			if (error) {
				setIntentionalSignOut(false);
				showNotification({
					message:
						"Beim Abmelden ist ein Fehler aufgetreten, bitte versuche es erneut",
					type: "error",
				});
				Logger.error(error);
				return;
			}
			set({
				userId: null,
				userEmail: null,
				organization: null,
				profile: null,
			});

			window.location.href = "/";
		} catch (error) {
			setIntentionalSignOut(false);
			Logger.error(error, {}, "Error signing out");
			showNotification({
				message:
					"Beim Abmelden ist ein Fehler aufgetreten, bitte versuche es erneut.",
				type: "error",
			});
		}
	},

	getLabs: async () => {
		const organizationId = get().organization?.id;
		if (!organizationId) return;
		

		const { data, error } = await supabase.rpc("get_brands", {
			p_org_id: organizationId,
		});

		if (error) {
			Logger.error(error);
			return;
		}

		const labsLookup = data.reduce((acc: Record<string, Lab>, row) => {
			acc[row.organization_id] = {
				name: row.name ?? "",
				labOrganizationId: row.organization_id,
			};

			return acc;
		}, {});

		set({
			labsLookup,
		});
	},

	getLabForms: async (labId: string) => {
		try {
			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.select("*")
				.eq("organization_id", labId);

			if (error) {
				Logger.error(error, {}, "Error fetching forms for lab");
				return;
			}

			// Parse the forms' definitions from JSON
			const parsedForms = data.map((form) => {
				return {
					...form,
					definition: form.definition as any,
					rules: form.rules as any,
				} as ExtendedFormTemplate;
			});

			// Update the store with the forms for this lab
			set((state) => ({
				labForms: {
					...state.labForms,
					[labId]: parsedForms,
				},
			}));
		} catch (error) {
			Logger.error(error, {}, "Error fetching forms for lab");
		}
	},
});
